import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDayLong } from "@/features/menu/menuLabels";
import {
  getNextWeekStart,
  getNextWeekThemePreference,
  setNextWeekThemePreference,
} from "@/features/menu/themePreference";
import { FIXED_THEMES, getRotatingTheme, type ThemeOption } from "./themes";

interface NextWeekPrepSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when the user confirms — theme is already saved to localStorage. */
  onGenerate: () => void;
}

/**
 * Ceremony sheet for generating next week's menu.
 *
 * Shows the next week's Monday date, a theme picker, and a prominent
 * "Générer mon menu" CTA. Selecting a theme saves it to localStorage
 * via setNextWeekThemePreference and then calls onGenerate.
 */
export function NextWeekPrepSheet({
  open,
  onOpenChange,
  onGenerate,
}: NextWeekPrepSheetProps) {
  const [selectedTheme, setSelectedTheme] = useState<string | null>(
    () => getNextWeekThemePreference() ?? "surprise",
  );
  const [rotatingTheme, setRotatingTheme] = useState<ThemeOption>(() =>
    getRotatingTheme(),
  );

  useEffect(() => {
    if (open) {
      setRotatingTheme(getRotatingTheme());
      setSelectedTheme(getNextWeekThemePreference() ?? "surprise");
    }
  }, [open]);

  const options: ThemeOption[] = [...FIXED_THEMES, rotatingTheme];
  const nextMondayLabel = formatDayLong(getNextWeekStart());

  function handleGenerate() {
    setNextWeekThemePreference(selectedTheme);
    onOpenChange(false);
    onGenerate();
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 mx-auto max-w-md border-t border-hair bg-background pb-[env(safe-area-inset-bottom)] shadow-soft-warm-lg data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-4 sm:bottom-auto sm:top-1/2 sm:max-w-lg sm:-translate-y-1/2 sm:border sm:rounded-none">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-hair px-6 py-4">
            <div>
              <Dialog.Title className="font-serif text-[15px] font-medium">
                Préparer la semaine prochaine
              </Dialog.Title>
              <p className="text-[12px] text-ink-50 capitalize">
                {nextMondayLabel}
              </p>
            </div>
            <button
              aria-label="Fermer"
              onClick={() => onOpenChange(false)}
              className="text-foreground/60 hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          </div>

          {/* Theme picker */}
          <div className="space-y-2 px-6 py-5">
            <p className="mb-3 text-[13px] text-ink-60 leading-relaxed">
              Choisissez une ambiance culinaire pour votre prochain batch. Le
              thème guidera la sélection des recettes.
            </p>
            {options.map((option) => {
              const isSelected =
                option.id === "surprise"
                  ? selectedTheme === "surprise" || selectedTheme === null
                  : selectedTheme === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setSelectedTheme(option.id)}
                  className={`flex w-full items-start gap-4 rounded-pill border px-4 py-3.5 text-left transition-colors ${
                    isSelected
                      ? "border-accent bg-accent/10"
                      : "border-hair bg-background hover:border-ink-30"
                  }`}
                >
                  <div
                    className={`mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full ${
                      isSelected
                        ? "bg-accent text-background"
                        : "bg-wash text-ink-50"
                    }`}
                  >
                    {option.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[14px] font-medium text-foreground">
                      {option.label}
                    </div>
                    <div className="text-[12px] text-ink-50">
                      {option.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Footer CTA */}
          <div className="flex items-center gap-3 border-t border-hair px-6 py-4">
            <Button
              variant="ghost"
              size="sm"
              className="flex-1"
              onClick={() => onOpenChange(false)}
            >
              Annuler
            </Button>
            <Button
              variant="default"
              size="sm"
              className="flex-1 gap-2"
              onClick={handleGenerate}
            >
              <Sparkles className="size-4" strokeWidth={1.5} />
              Générer mon menu
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
